import React, {useMemo, useState} from "react";
import {useIntl} from "react-intl";
import {useQuery} from "@tanstack/react-query";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {Table, TableData} from "peggirkit";
import {FeaturedProductDetails} from "@/data/entities/product";
import {getFeaturedProductsTableColumns} from "@widgets/featuredProducts/overview/table/FeaturedProductsTableColumns";
import UpdateOrdering from "@widgets/featuredProducts/overview/ordering/UpdateOrdering";
import DeleteFeaturedProductModal, {
    DeleteFeaturedProductState
} from "@widgets/featuredProducts/delete/DeleteFeaturedProductModal";
import {api} from "@/data/api";


const FeaturedProductsTable = () => {
    const intl = useIntl();
    const {dashboardLocation} = useRestaurant();
    const [deleteModal, setDeleteModal] = useState<DeleteFeaturedProductState>({
        showModal: false,
        featuredProductId: -1,
    });
    const [tableData, setTableData] = useState<TableData<FeaturedProductDetails>[]>([]);
    const {isFetching} = useQuery({
        queryKey: ["featured-products", dashboardLocation.id],
        queryFn: async () => {
            const result: FeaturedProductDetails[] = await api.featuredProduct.listFeaturedProducts(dashboardLocation.id);
            setTableData(result);
            return result;
        },
    });
    const columns = useMemo(
        () => getFeaturedProductsTableColumns(intl, setDeleteModal),
        [intl, setDeleteModal]);

    return (
        <div className={"flex flex-col gap-4"}>
            <Table<FeaturedProductDetails>
                columns={columns}
                data={tableData}
                setData={setTableData}
                loading={isFetching}
                loadingConfig={{
                    skeletonRepeat: 4,
                }}
                rowConfig={{
                    draggableRows: {
                        onDragEnd: ({swapRows}) => {
                            swapRows();
                        },
                    },
                }}
            />

            <UpdateOrdering<FeaturedProductDetails>
                isUpdatingOverview={isFetching}
                persistFn={api.featuredProduct.updateFeaturedProductsOrdering}
                tableData={tableData}
            />

            <DeleteFeaturedProductModal
                show={deleteModal.showModal}
                setShow={(show: boolean) => setDeleteModal(prev => ({...prev, showModal: show}))}
                featuredProductId={deleteModal.featuredProductId}
                cb={(remainingFeaturedProducts: FeaturedProductDetails[]) => {
                    setTableData(remainingFeaturedProducts);
                }}
            />
        </div>
    );
};

export default FeaturedProductsTable;